import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WhatsNewItem } from '../../core/model/types';
import { loadWhatsNew } from '../../core/whatsNew/whatsNew';
import { Sheet } from '../common/Sheet';
import { formatIsoDate } from '../common/format';

type Loaded = { status: 'loading' } | { status: 'error' } | { status: 'ok'; items: WhatsNewItem[] };

// The app's changes, newest first, opened from the menu.
export function WhatsNew({ onClose }: { onClose(): void }) {
    const { t, i18n } = useTranslation();
    const [loaded, setLoaded] = useState<Loaded>({ status: 'loading' });
    const [attempt, setAttempt] = useState(0);
    const retry = useCallback(() => {
        setLoaded({ status: 'loading' });
        setAttempt(value => value + 1);
    }, []);

    useEffect(() => {
        let active = true;
        loadWhatsNew().then(
            items => { if (active) setLoaded({ status: 'ok', items }); },
            () => { if (active) setLoaded({ status: 'error' }); },
        );
        return () => { active = false; };
    }, [attempt]);

    let content;
    if (loaded.status === 'loading') {
        content = <p className="state-message" role="status">{t('state_loading')}</p>;
    } else if (loaded.status === 'error') {
        content = (
            <div className="state-message" role="alert">
                <p>{t('whatsNew_error')}</p>
                <button type="button" onClick={retry}>{t('state_retry')}</button>
            </div>
        );
    } else if (loaded.items.length === 0) {
        content = <p className="state-message">{t('whatsNew_empty')}</p>;
    } else {
        content = (
            <ol className="whatsnew-list">
                {loaded.items.map(item => (
                    <li key={item.id} className="whatsnew-item">
                        <span className="whatsnew-date mono muted">{formatIsoDate(item.date, i18n.language)}</span>
                        <h3 className="whatsnew-title">{item.title}</h3>
                        {item.body !== '' && <p className="whatsnew-body">{item.body}</p>}
                    </li>
                ))}
            </ol>
        );
    }

    return (
        <Sheet title={t('whatsNew_title')} doneLabel={t('common_done')} onClose={onClose}>
            {content}
        </Sheet>
    );
}
